import opConfig from 'openpathConfig';
import { stringify } from 'query-string';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'components/Button';
import { Loader } from 'components/Loader';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSection } from 'new-components/DLS/OpSection/OpSection';
import { appColorThemeManager } from 'utils/appColorThemeManager';
import { useFeatureGate } from 'utils/customHooks/useFeatureGate';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { MessageTypes } from 'utils/iframeMessaging';
import { getAccessToken } from 'utils/accessToken';
import { useOpMutation } from 'utils/customHooks/useOpMutation';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { accountPageRoute } from 'routes/constants';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { isDevelopmentEnv } from 'constants/envs';

import { FEATURE_GATE_URL_DICTIONARY } from './constants';

import './FeatureGate.scss';

export type FeatureCode = keyof typeof FEATURE_GATE_URL_DICTIONARY;
type FeatureGateUrlDictionary = typeof FEATURE_GATE_URL_DICTIONARY;
type FeatureGateFeatureName = keyof FeatureGateUrlDictionary;
type FeatureInfo =
  | FeatureGateUrlDictionary[FeatureGateFeatureName]['channel']
  | FeatureGateUrlDictionary[FeatureGateFeatureName]['revshare'];

interface FeatureGateProps {
  activateOnClick?: boolean;
  classes?: {
    overlay?: string;
    root?: string;
  };
  children: ({
    disabled,
    enabled,
  }: {
    disabled: boolean;
    enabled: boolean;
  }) => ReactNode;
  featureCode: FeatureCode;
  loader?: boolean;
  orgId: number;
  onModalButtonPress?: () => void;
}

export const FeatureGate = ({
  activateOnClick,
  classes,
  children,
  featureCode,
  loader,
  orgId,
  onModalButtonPress,
}: FeatureGateProps) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const accessToken = getAccessToken();
  const navigate = useNavigate();

  // State
  const [isHovered, setIsHovered] = useState(false);
  const [showMarketingLoader, setShowMarketingLoader] = useState(true);
  const [isModalVisible, setModalVisible] = useState(false);

  const { hasFeature } = useFeatureGate(featureCode, orgId);
  const disabled = Boolean(featureCode) && !hasFeature;

  const { data: allFeatures = [] } = useOpQuery({
    apiEndpointName: 'listOrgAllFeatures',
    parameters: [orgId],
    select: (res) => res.json.data,
  });

  const { data: org } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (res) => res.json.data,
  });

  const { data: tokenData } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    select: (data) => data.json.data,
  });

  const { data: identity } = useOpQuery({
    apiEndpointName: 'describeIdentity',
    parameters: [tokenData?.identity?.id!],
    select: (data) => data?.json?.data,
  });

  // Get the color theme from the identity preferences
  const { data: colorTheme } = useOpQuery({
    apiEndpointName: 'describeIdentityPreferenceSet',
    parameters: [Number(identity?.id)],
    select: (heliumResponse) => heliumResponse?.json?.data?.colorTheme,
  });

  const { mutateAsync: requestPackageUpdate } = useOpMutation({
    apiEndpointName: 'requestPackageUpgrade',
  });

  const billingMode = org?.billingMode;
  const currentUserId = tokenData?.tokenScopeList?.find(
    (tokenScope) => tokenScope.org?.id === orgId,
  )?.user?.id;

  // dictionary
  const featureInfo = useRef<FeatureInfo>(undefined);

  useEffect(() => {
    // We only want to run the code if we have a featureCode
    // (featureCode will be null if it is not passed to FeatureGate due to default props)
    if (featureCode) {
      // NOTE - this might be bad, but we don't want to render while we're waiting for the selector

      if (org?.billingMode === undefined) return;

      try {
        featureInfo.current =
          FEATURE_GATE_URL_DICTIONARY[featureCode][
            billingMode === 'channel' ? 'channel' : 'revshare'
          ];
      } catch {
        const msg = t(
          `Unable to find upgrade URL for featureCode: {{featureCode}} with billing mode: {{billingMode}}.`,
          {
            featureCode,
            billingMode,
          },
        );
        featureInfo.current =
          FEATURE_GATE_URL_DICTIONARY.generic[
            billingMode === 'channel' ? 'channel' : 'revshare'
          ];
        console.error(msg);
      }
    }
  }, [t, featureCode, billingMode, org?.billingMode]);

  const requestPackageUpgradeWithCode = useCallback(async () => {
    await requestPackageUpdate({
      apiEndpointRequirements: [orgId],
      payload: {
        featureCode,
        requestedAt: new Date().toISOString(),
        userId: Number(currentUserId),
      },
    });

    if (billingMode === 'channel') {
      notificationService.show('success', t('Request sent to partner'));
      return;
    }

    navigate(accountPageRoute, {
      state: {
        forceSubscriptionEdit: true,
      },
    });
  }, [
    billingMode,
    currentUserId,
    featureCode,
    navigate,
    orgId,
    requestPackageUpdate,
    t,
  ]);

  // Handlers
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = useCallback(() => {
    // don't let the upgrade flow close if they mouse out of the window
    if (isModalVisible) return;
    setIsHovered(false);
  }, [isModalVisible]);

  const urlParams = useMemo(
    () =>
      stringify(
        {
          firstname: identity?.firstName,
          email: identity?.email,
          company: org?.name,
          orgid: orgId,
          featureCode,
          billingMode,
          darkMode:
            appColorThemeManager.getColorThemeForProvider(colorTheme) ===
            'dark', // To show the iFrame in dark mode or not
        },
        { strict: false },
      ),
    [
      billingMode,
      colorTheme,
      identity?.email,
      identity?.firstName,
      orgId,
      org?.name,
      featureCode,
    ],
  );

  let envUrl = 'https://modal.prod.control.openpath.com';
  if (!opConfig.ENV || isDevelopmentEnv(opConfig.ENV)) {
    envUrl = 'https://modal.dev.control.openpath.com';
  }

  useEffect(() => {
    const messageHandler = (e: MessageEvent) => {
      // ignore messages coming from any other origin
      if (![envUrl].includes(e.origin)) return;
      // ignore messages we don't care about
      if (!Object.keys(MessageTypes).includes(e.data.type)) return;
      switch (e.data.type) {
        case 'PLATINUM_MESSAGE_TEST':
          console.log(`MESSAGE FROM IFRAME ${JSON.stringify(e.data)}`); // eslint-disable-line no-console
          break;
        case 'PARENT_REDIRECT':
          requestPackageUpgradeWithCode().catch(console.error);
          break;
        case 'DESTROY_CHILD':
          if (e.data.data) {
            setIsHovered(false);
          }
          break;
        case 'CHILD_CONTENT_READY':
          if (e.data.data) {
            setShowMarketingLoader(false);
          }
          break;
        default:
          console.error(`UNHANDLED MESSAGE TYPE FROM IFRAME ${e.data.type}`);
          break;
      }
    };
    // listen for messages from the iFrame
    window.addEventListener('message', messageHandler);
    // send a test message every 5 seconds into the iframe
    // setInterval(() => sendMessageToIframe(MessageTypes.PLATINUM_MESSAGE_TEST, `Hello ${Date.now()}`), 5000)
    return () => window.removeEventListener('message', messageHandler);
  }, [featureCode, billingMode, envUrl, requestPackageUpgradeWithCode]);

  const onOpenCallback = useCallback(() => {
    setTimeout(() => setShowMarketingLoader(false), 10000);
  }, []);

  const onCloseCallback = useCallback(() => {
    setModalVisible(false);
    setShowMarketingLoader(true);
  }, []);

  const renderUpgradeText = () => {
    const featureName =
      allFeatures?.find(({ code }) => code === featureCode)?.name ||
      featureCode;

    return (
      <h4>
        {featureInfo?.current?.popUpCopy
          ? t(featureInfo.current.popUpCopy, {
              featureName,
            })
          : t('This is a Premium Feature')}
      </h4>
    );
  };

  const renderUpgradeButton = (customTrigger: ReactNode) => {
    /**
     * I am using a new prop onModalButtonPress
     * for now to be safe as I have no idea what
     * customTrigger was / is being used for. We
     * are revisiting the feature gate so this should
     * be sufficient for now.
     */
    if (onModalButtonPress) {
      return (
        <Button
          id={`${featureCode}-upgrade-button`}
          data-gtm={`${featureCode}-feature-gate-button`}
          primary
          fluid
          icon="arrow circle up"
          content={
            featureInfo?.current?.buttonCta
              ? t(featureInfo.current.buttonCta)
              : t('More information')
          }
          labelPosition="left"
          onClick={onModalButtonPress}
        />
      );
    }

    if (customTrigger) {
      return customTrigger;
    }
  };

  // ** new simple version for things like buttons and not sections...
  if (activateOnClick) {
    return disabled
      ? renderUpgradeButton(children({ disabled, enabled: !disabled }))
      : children({ disabled, enabled: !disabled });
  }

  // we we didn't pass a feature code, just ignore everything
  if (!featureCode) return children({ disabled: false, enabled: true });

  return (
    <>
      <div
        className={clsx('feature-gate', classes?.root)}
        data-testid="feature-gate"
      >
        {/* Children (need position property in order for overlay to work properly */}
        {children({ disabled, enabled: !disabled })}

        {/* Gate (only show when the feature is disabled */}
        {disabled &&
          (allFeatures?.length > 0 ? (
            <div
              className={clsx('feature-gate__overlay-container', {
                'feature-gate__overlay-container--hover': isHovered,
              })}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              data-testid="feature-gate-overlay-container"
            >
              {isHovered && (
                <OpSection
                  className={clsx('feature-gate__overlay', classes?.overlay)}
                  testId="feature-gate-overlay"
                >
                  <div className="feature-gate__message">
                    {renderUpgradeText()}

                    <OpDivider />

                    <Button
                      id={`${featureCode}-upgrade-button`}
                      data-gtm={`${featureCode}-feature-gate-button`}
                      primary
                      fluid
                      icon="arrow circle up"
                      content={
                        featureInfo?.current?.buttonCta
                          ? t(featureInfo.current.buttonCta)
                          : t('More information')
                      }
                      labelPosition="left"
                      onClick={() => setModalVisible(true)}
                    />
                  </div>
                </OpSection>
              )}
            </div>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>{loader && <Loader content={t(`Loading features...`)} />}</>
          ))}
      </div>

      <OpModal
        className="feature-gate__modal"
        closeIcon={false}
        footer={[
          <OpButton key="close-button" onClick={onCloseCallback}>
            Close
          </OpButton>,
        ]}
        open={isModalVisible}
        afterOpenChange={onOpenCallback}
        onCancel={onCloseCallback}
      >
        <>
          {showMarketingLoader && <Loader />}
          <iframe
            style={{ border: 'none', zIndex: 0, position: 'relative' }}
            ref={iframeRef}
            width="100%"
            height="600"
            id="featureCodeUpgradeFrame"
            title="featureCodeUpgradeForm"
            src={`${envUrl}${featureInfo.current?.url}?${urlParams}`}
          />
        </>
      </OpModal>
    </>
  );
};
